import { useEffect, useState } from "react";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";

import { handlePagingAll } from "../../../../components/api/APIUtilities";
import {
  BCPPen,
  BCPSquarePlus,
  BCPTrash
} from "../../../../components/icons/bcp/BCPIcons";
import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import { postNotification } from "../../../../components/utilities/Notifications";
import {
  cleanLocation,
  deleteData,
  postData
} from "../../../../components/utilities/UtilityFunctions";
import { LocationModal } from "../../../../components/utilities/LocationPicker";
import { getLanguageCookie } from "../../../../language/LanguageFunctions";

const sortLocations = (a, b) =>
  (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase());

export default function LocationsTab(props) {
  const { authContext } = props;
  const userId = authContext?.authState?.identity?.userId;
  const [locations, setLocations] = useState(null);
  const [modalLoc, setModalLoc] = useState(null);
  const [processing, setProcessing] = useState(false);
  const confirm = useConfirm();

  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "pages.account.locations"
  });

  useEffect(() => {
    const go = async () => {
      try {
        const apiLocations = await handlePagingAll("/locations", { userId });
        setLocations(
          (apiLocations || []).filter((loc) => !loc.isOnline).sort(sortLocations)
        );
      } catch (err) {
        postNotification("error", err);
        setLocations([]);
      }
    };
    if (userId) go();
  }, [userId]);

  const handleSave = async (loc) => {
    if (!loc?.coordinate || !loc?.timeZone || !loc?.country) {
      postNotification(
        "error",
        t("incompleteLocation", {
          defaultValue:
            "Please pick an address from the search results so we can find its coordinates, time zone and country."
        })
      );
      return;
    }
    try {
      let apiData;
      if (modalLoc?.id) {
        apiData = await postData(`/locations/${modalLoc.id}`, {
          body: { set: { ...loc, ownerId: userId } }
        });
        setLocations(
          locations
            .map((el) => (el.id === apiData.id ? apiData : el))
            .sort(sortLocations)
        );
      } else {
        apiData = await postData("/locations", {
          body: { ...loc, ownerId: userId }
        });
        setLocations([apiData, ...locations].sort(sortLocations));
      }
      setModalLoc(null);
    } catch (err) {
      postNotification("error", err);
    }
  };

  const handleDelete = async (loc) => {
    try {
      await confirm({
        title: t("areYouSure", { defaultValue: "Are you sure?" }),
        description: t("deleteLocationConfirmation", {
          defaultValue:
            "Are you sure you want to delete this location? Events that already use it will keep showing it."
        })
      });
    } catch (err) {
      return;
    }
    try {
      setProcessing(true);
      await deleteData(`/locations/${loc.id}`);
      setLocations(locations.filter((el) => el.id !== loc.id));
    } catch (err) {
      postNotification("error", err);
    }
    setProcessing(false);
  };

  return (
    <Box sx={{ pt: 1 }}>
      {(locations === null || processing) && <ModalLoadingFull />}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" component="h2" gutterBottom>
            {t("title", { defaultValue: "Saved Locations" })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("description", {
              defaultValue:
                "Locations you save here can be picked when you create events and set up the BCP Discord bot."
            })}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="outlined"
            startIcon={<BCPSquarePlus />}
            onClick={() => setModalLoc({})}
          >
            {t("addLocation", { defaultValue: "Add location" })}
          </Button>
        </Grid>
        {locations?.length === 0 && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
              <Typography gutterBottom>
                {t("emptyState", {
                  defaultValue: "You haven't saved any locations yet."
                })}
              </Typography>
              <Button variant="contained" onClick={() => setModalLoc({})}>
                {t("addFirstLocation", {
                  defaultValue: "Add your first location"
                })}
              </Button>
            </Paper>
          </Grid>
        )}
        {locations?.map((loc) => (
          <Grid item xs={12} key={loc.id}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm>
                  <Typography fontWeight={700}>{loc.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {cleanLocation(loc) || loc.addressString}
                  </Typography>
                </Grid>
                <Grid item xs="auto">
                  <Button
                    variant="contained"
                    aria-label={t("edit", { defaultValue: "Edit" })}
                    onClick={() => setModalLoc(loc)}
                  >
                    <BCPPen />
                  </Button>
                </Grid>
                <Grid item xs="auto">
                  <Button
                    variant="contained"
                    color="error"
                    aria-label={t("delete", { defaultValue: "Delete" })}
                    onClick={() => handleDelete(loc)}
                  >
                    <BCPTrash />
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <LocationModal
        location={modalLoc}
        setLocation={setModalLoc}
        onSave={handleSave}
      />
    </Box>
  );
}
